import { Document, HeadingLevel, Packer, Paragraph, TextRun } from 'docx';

// Segment shape shared by export-toolbar (Speaker interface) and LibraryPage (projected from Segment[])
interface WordSegment {
  speaker: string;
  text: string;
  timestamp?: string;
}

interface BuildDocxBlobParams {
  title: string;
  segments: WordSegment[];
  summary?: string;
}

export function docxFilenameFromTitle(title: string): string {
  return `${title.replace(/[^a-z0-9]/gi, '_').substring(0, 50)}.docx`;
}

export async function buildDocxBlob({
  title,
  segments,
  summary,
}: BuildDocxBlobParams): Promise<Blob> {
  const speakers = [...new Set(segments.map((s) => s.speaker))];
  const children: Paragraph[] = [];

  // ── Title ───────────────────────────────────────────────────────────────
  children.push(
    new Paragraph({
      text: title,
      heading: HeadingLevel.HEADING_1,
    })
  );

  // ── Summary (single-transcript path only; Library omits this) ───────────
  if (summary) {
    children.push(new Paragraph({ text: '' }));
    children.push(
      new Paragraph({
        text: 'Summary',
        heading: HeadingLevel.HEADING_2,
      })
    );
    children.push(new Paragraph({ text: summary }));
  }

  // ── Speakers list ────────────────────────────────────────────────────────
  children.push(new Paragraph({ text: '' }));
  children.push(
    new Paragraph({
      text: 'Speakers',
      heading: HeadingLevel.HEADING_2,
    })
  );
  for (const speaker of speakers) {
    children.push(new Paragraph({ text: `\u2022 ${speaker}` }));
  }

  // ── Transcript ───────────────────────────────────────────────────────────
  children.push(new Paragraph({ text: '' }));
  children.push(
    new Paragraph({
      text: 'Transcript',
      heading: HeadingLevel.HEADING_2,
    })
  );
  children.push(new Paragraph({ text: '' }));

  for (const seg of segments) {
    const ts = seg.timestamp ? ` (${seg.timestamp})` : '';

    // Bold speaker label + optional timestamp
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `${seg.speaker}${ts}`,
            bold: true,
          }),
        ],
      })
    );

    // Segment body text
    children.push(
      new Paragraph({
        children: [new TextRun({ text: seg.text })],
      })
    );

    // Blank spacer between segments
    children.push(new Paragraph({ text: '' }));
  }

  // Build and return the binary blob
  const doc = new Document({
    sections: [
      {
        properties: {},
        children,
      },
    ],
  });

  return Packer.toBlob(doc);
}
